import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Building2,
  BadgeCheck,
  CircleSlash,
  MapPin,
  ExternalLink,
  Briefcase,
} from "lucide-react";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import InnerLoader from "../../../component/ui/loader/inner-loader";
import { Alert, Snackbar } from "@mui/material";
import { GlobalContext } from "../../../context/Context";
import DataNotFound from "../../../component/ui/loader/data-not-found";
import AssignVanModal from "../../../component/popup/assign-van";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span className={classNames("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", tones[tone])}>
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default", valueColor="" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500">{title}</div>
          <div className={`text-lg font-semibold break-all !text-[16px] tracking-tight capitalize ${valueColor}`}>{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

export default function VanQuickView({ open = true, onClose = () => { }, data, onReload = () => {} }) {

  let { state } = useContext(GlobalContext);

  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState({});
  const [alertData, setAlertData] = useState({ isShow: false });
  const [assignModal, setAssignModal] = useState(false);

  const handleClose = () => {
    setAlertData({ isShow: false })
  }

  const getData = async () => {
    let apiData = `get-van-fleet-details/${data?.id}`;

    try {
      let res = await apiCall({ method: 'get', url: apiData });
      setDetails(res?.ven_detail);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error)
    }
  }

  // useOnClickOutside(containerRef, () => onClose());

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    // if (data) {
    //   if (isCheck) {
    //     setIsLoading(true)
    //   } else {
    //     getData();
    //   }
    // }
    getData();
  }, [data]);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message })
    getData();
    onReload();
    setAssignModal(false);
  }

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message })
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[1000px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
              aria-modal
              role="dialog"
            >
              {
                isLoading ? (
                  <>
                    {/* Header */}
                    <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full relative z-30">
                      <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                        <div className="flex min-w-0 items-center gap-3">
                          {
                            details?.car_image ?

                            <img
                              src={details?.car_image}
                              alt={details?.van_entitlement}
                              className="size-12 rounded-2xl object-cover ring-2 ring-white shadow"
                            />
                            :
                            <div className="size-12 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-sm font-medium">{details?.van_entitlement?.split(" ")?.[0]?.charAt(0)}{details?.van_entitlement?.split(" ")?.[1]?.charAt(0)}</div>
                          }
                          <div className="min-w-0">
                            <div className="flex-col">
                              <div className="flex gap-2 items-center">
                                <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                                  {details?.van_entitlement}
                                </h2>
                                <Badge tone={details?.status == 1 ? "green" : "red"} icon={details?.status == 1 ? BadgeCheck : CircleSlash}></Badge>
                              </div>
                              <div className="flex gap-2 items-center">
                                <Badge tone="blue" icon={Briefcase}>{details?.vendor_details?.vendor_company_name}</Badge>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <div
                            className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            onClick={() => {setAssignModal(true)}}
                          >
                            Assign
                          </div>
                          <button
                             className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                            onClick={onClose}
                            aria-label="Close"
                          >
                            <X className="size-4" /> Exit
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="grid min-h-0 flex-1 grid-cols-12 gap-4 w-full overflow-hidden p-4">
                      {/* Left: main content */}
                      <div className="col-span-12 flex min-h-0 flex-col w-full gap-4 lg:col-span-12">
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                          <Stat title={"Contact"} value={details?.contact_number || "-"} />
                          <Stat title={"Shift"} value={details?.shift || "-"} />
                          <Stat title={"Registration Number"} value={details?.registration_number || "-"} />
                          <Stat title={"Route"} value={details?.route ?? "-"} />
                        </div>

                        {/* <div className="flex items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-1">
                            <button
                              // key={t.key}
                              onClick={() => setActiveTab("Mileage")}
                              className={classNames(
                                "rounded-xl px-3 py-2 text-sm font-medium",
                                activeTab == "Mileage"
                                  ? "bg-slate-900 text-white shadow"
                                  : "text-slate-600 hover:bg-slate-100"
                              )}
                            >
                              {"Mileage"}
                            </button>

                            <button
                              // key={t.key}
                              onClick={() => setActiveTab("Maintenance")}
                              className={classNames(
                                "rounded-xl px-3 py-2 text-sm font-medium",
                                activeTab == "Maintenance"
                                  ? "bg-slate-900 text-white shadow"
                                  : "text-slate-600 hover:bg-slate-100"
                              )}
                            >
                              {"Maintenance"}
                            </button>
                          </div>
                        </div> */}

                        <div className="min-h-0 overflow-auto rounded-xl w-full pb-2 relative">
                          { details?.employee_details?.length ?
                            <div className="grid grid-cols-1 gap-3">
                              <div className={`sticky top-0 grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-2 flex items-center gap-2 text-[13px] font-medium text-slate-800 capitalize">
                                  Name
                                </div>

                                <div className="col-span-2 flex items-center gap-2 text-[13px] font-medium text-slate-800 capitalize">
                                  Department
                                </div>

                                <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-800 capitalize">
                                  Address
                                </div>
                              </div>
                              {details?.employee_details?.map((eachList, i) => (
                                <div key={i} className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                  <div className="col-span-2 flex gap-2 text-[13px] font-medium text-slate-500">
                                    <div className="flex items-center gap-1">
                                      {
                                        (eachList?.profile_picture) ? (
                                          <img className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-xs font-medium object-cover object-center" src={eachList?.profile_picture} alt={eachList?.name} />
                                        ) : (
                                          <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-xs font-medium">{eachList?.name?.split(" ")[0] ? eachList?.name?.split(" ")[0][0] : ""}{eachList?.name?.split(" ")[1] ? eachList?.name?.split(" ")[1][0] : ""}</div>
                                        )
                                      }
                                      <span>{eachList?.name || "-"}</span>
                                    </div>
                                  </div>
                                  <div className="col-span-2 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                    <span>{eachList?.emp_dept ? eachList?.emp_dept : "-"}</span>
                                  </div>

                                  <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                    <span>{eachList?.emp_address ?? "-"}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            :
                            <DataNotFound title={"Employee"} />
                          }
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <InnerLoader wrapperClass={'w-full'} />
                )
              }
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
      {
        assignModal &&
        <AssignVanModal vanId={details?.id} selectedEmp={details?.employee_details} onClose={() => { setAssignModal(false) }} onError={onError} onSubmit={onSuccess} />
      }
      <Snackbar open={alertData?.isShow} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
}